<script lang="ts">
  import { cn } from '$lib/utils';
  import { snapCenterScrollPosition } from '$lib/actions/snap-center-scroll-position';
  import type { Look } from './look.svelte';

  import { createEventDispatcher, onMount, onDestroy } from 'svelte';

  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import { getHash } from '$lib/utils/strings';
  import { buildSrcSet } from '$lib/image';
  import { getUUID } from '$lib/utils/uuid';
  import { AddToCartLocations, analyticsEvent, AnalyticsEvents } from '$lib/analytics';
  import { lang } from '$lib/lang';
  import CmsImage from '$components/cms-image.svelte';
  import QuickAddCardLoader from '$components/quick-add-card-loader.svelte';
  import QuickAddCard from '$components/quick-add-card.svelte';
  import SliderButton from './ui/slider-button.svelte';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import Button from './ui/button.svelte';
  import Skeleton from './ui/skeleton.svelte';

  const CARD_WIDTH = 280;

  export let look: Look;
  export let variant: 'card' | 'drawer' = 'card';
  export let showInProductsGrid: boolean | undefined = undefined;

  const dispatch = createEventDispatcher<{ click: undefined }>();

  $: markers = look.markers?.details;
  $: productsWithoutMarkers =
    look.products?.reduce<string[]>((acc, product) => {
      if (product.link && typeof product.link === 'object') {
        const link = $getAnchorFromCmsLink(product.link);
        const handle = link?.href?.split('/').pop();

        acc.push(handle || '');
      } else if (product.handle) {
        acc.push(product.handle);
      }

      return acc;
    }, []) || [];

  $: productHandles = [...productsWithoutMarkers, ...(markers ? markers.map((m) => m.title) : [])];

  $: markersSrc = look.markers?.image?.src;

  const targetId = 'shop-the-look' + getUUID();

  let isExpanded = false;
  let content: HTMLParagraphElement;
  let height = 15;
  let carousel: HTMLElement;
  let carouselIdx = 0;

  // Image
  $: markersImageAsset = markersSrc
    ? {
        id: getHash(markersSrc),
        filename: markersSrc,
        alt: look.name || '',
        name: look.name || '',
        focus: null,
        source: null,
        title: null,
        copyright: null
      }
    : undefined;

  $: sbImageAsset = look.image
    ? {
        id: getHash(look.image),
        filename: look.image,
        alt: look.caption || '',
        name: look.username || '',
        focus: null,
        source: null,
        title: null,
        copyright: null
      }
    : undefined;

  const handleToggleExpand = (event: MouseEvent) => {
    event.stopPropagation();
    isExpanded = !isExpanded;
    if (isExpanded) {
      height = content?.scrollHeight; // expand to full height
    } else {
      height = 15; // collapse
    }
  };

  const handleCarousel = (
    to: 'left' | 'right' | number,
    behavior: 'instant' | 'smooth' = $reducedMotion ? 'instant' : 'smooth'
  ) => {
    if (to === 'left' && carouselIdx !== null) {
      carousel.scrollTo({
        left: CARD_WIDTH * 0.9 * (carouselIdx - 1),
        behavior
      });
    } else if (to === 'right' && carouselIdx !== null) {
      carousel.scrollTo({
        left: CARD_WIDTH * 0.9 * (carouselIdx + 1),
        behavior
      });
    } else if (typeof to === 'number') {
      carousel.scrollTo({
        left: CARD_WIDTH * 0.9 * to,
        behavior
      });
    }
  };

  const handleClick = (event: MouseEvent) => {
    const button = event.currentTarget as HTMLElement;
    const target = event.target as HTMLElement;

    // Allow if clicked on the button itself or its immediate children
    if (target === event.currentTarget || target.parentElement === button) {
      dispatch('click');
    } else {
      return;
    }
  };

  $: hasDetails =
    look.username || look.date || look.caption || (look.avatar && look.avatar.filename);

  // Image
  let host: HTMLElement;
  let img: HTMLImageElement;

  let imgWidth = 0;
  let imgHeight = 0;
  $: imgAspectRatio = imgWidth / imgHeight;
  $: hostAspectRatio = host?.clientWidth / host?.clientHeight;

  $: isImageWiderThanContainer = hostAspectRatio < imgAspectRatio;

  let showMedia = false;
  let observer: IntersectionObserver | null = null;

  onMount(() => {
    // Image natural dimensions for layout references
    if (markersImageAsset) {
      img = new Image();
      img.src = getImageAttributes(markersImageAsset, { size: [10, 0] }).src;

      img.onload = () => {
        imgWidth = img.naturalWidth;
        imgHeight = img.naturalHeight;
      };
    }

    if (!host) return;
    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          showMedia = true;
          observer?.disconnect();
        }
      },
      { threshold: 0.01 }
    );
    observer.observe(host);
  });

  onDestroy(() => {
    observer?.disconnect();
  });
</script>

<button
  class="w-full h-full flex justify-center items-center"
  style="container-type: size;"
  id={targetId}
  on:click={handleClick}
  bind:this={host}
>
  {#if showMedia}
    {#if look.video && look.video.filename}
      <video
        src={look.video.filename}
        class="absolute inset-0 -z-10 h-full w-full select-none object-cover"
        autoplay
        loop
        muted
        playsinline
      />
    {:else if markersImageAsset && markersImageAsset.filename && markers && markers.length > 0 && !look.use_markers_with_following_image}
      <div class="relative flex">
        <img
          src={markersImageAsset.filename}
          alt={look.name}
          srcset={buildSrcSet({
            loader: ({ width }) => getImageAttributes(markersImageAsset, { size: [width, 0] }).src,
            widths: variant === 'drawer' ? [500] : [400]
          })}
          sizes={variant === 'drawer' ? '500px' : '400px'}
          loading="lazy"
          draggable="false"
          class={cn(
            '-z-10 select-none max-w-none',
            isImageWiderThanContainer ? 'h-[100cqh]' : 'w-[100cqw]'
          )}
        />

        {#if !showInProductsGrid}
          {#each markers || [] as marker, i}
            {#if marker}
              <div
                class={cn(
                  'absolute z-10 p-1 -translate-x-1/2 -translate-y-1/2 outline-none opacity-0',
                  'bg-primary/32 backdrop-blur rounded-full',
                  'transition-all duration-500 motion-reduce:transition-none',
                  i === carouselIdx && 'opacity-100'
                )}
                style:top="{marker.y || 0}%"
                style:left="{marker.x || 0}%"
              >
                <div class={cn('box-content h-1.5 w-1.5 rounded-full bg-highlight ')} />
              </div>
            {/if}
          {/each}
        {/if}
      </div>
    {:else if look.image}
      <div class={cn(look.use_markers_with_following_image && 'relative flex')}>
        {#if look.image?.includes('storyblok.com')}
          <img
            loading="lazy"
            class={cn(
              look.use_markers_with_following_image
                ? '-z-10 select-none max-w-none'
                : 'absolute inset-0 -z-10 h-full w-full select-none object-cover',
              isImageWiderThanContainer ? 'h-[100cqh]' : 'w-[100cqw]'
            )}
            src={sbImageAsset?.filename}
            alt={look.caption}
            srcset={buildSrcSet({
              loader: ({ width }) => getImageAttributes(sbImageAsset, { size: [width, 0] }).src,
              widths: variant === 'drawer' ? [500] : [400]
            })}
            sizes={variant === 'drawer' ? '500px' : '400px'}
          />
        {:else}
          <!-- Yotpo or shopify image -->
          <img
            src={look.image}
            loading="lazy"
            alt={look.caption}
            class={cn(
              look.use_markers_with_following_image
                ? '-z-10 select-none max-w-none'
                : 'absolute inset-0 -z-10 h-full w-full select-none object-cover',
              isImageWiderThanContainer ? 'h-[100cqh]' : 'w-[100cqw]'
            )}
          />
        {/if}

        {#if look.use_markers_with_following_image && !showInProductsGrid}
          {#each markers || [] as marker, i}
            {#if marker}
              <div
                class={cn(
                  'absolute z-10 p-1 -translate-x-1/2 -translate-y-1/2 outline-none opacity-0',
                  'bg-primary/32 backdrop-blur rounded-full',
                  'transition-all duration-500 motion-reduce:transition-none',
                  i === carouselIdx && 'opacity-100'
                )}
                style:top="{marker.y || 0}%"
                style:left="{marker.x || 0}%"
              >
                <div class={cn('box-content h-1.5 w-1.5 rounded-full bg-highlight ')} />
              </div>
            {/if}
          {/each}
        {/if}
      </div>
    {/if}
  {:else}
    <Skeleton class="h-full w-full" />
  {/if}
</button>

{#if !showInProductsGrid}
  {#if variant == 'card' && productHandles && productHandles.length > 1}
    <div class={cn('absolute left-2 top-1/2 -translate-y-1/2 lg:right-3 z-10 hidden lg:block')}>
      <SliderButton
        variant="ghost"
        icon="chevron-left-bold"
        ariaLabel="Slider Button Left"
        disabled={carouselIdx === 0}
        on:click={() => handleCarousel('left')}
      />
    </div>

    <div class={cn('absolute right-2 top-1/2 -translate-y-1/2 lg:right-3 z-10 hidden lg:block')}>
      <SliderButton
        variant="ghost"
        icon="chevron-right-bold"
        ariaLabel="Slider Button Right"
        disabled={carouselIdx === productHandles.length - 1}
        on:click={() => handleCarousel('right')}
      />
    </div>
  {/if}

  <!-- Details and ProductCards -->
  <div
    class={cn(
      'absolute bottom-0 pb-2 pt-10 w-full z-10',
      hasDetails && 'bg-gradient-to-t from-primary/80 via-primary/80 to-primary/0'
    )}
  >
    <div class="flex px-3.5 mb-3 gap-4.5 w-full justify-end">
      <!-- Details -->
      {#if hasDetails}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          role="button"
          tabindex="0"
          class="w-full text-left p-0 text-sm cursor-pointer overflow-hidden flex gap-2"
          on:click={handleToggleExpand}
        >
          {#if look.avatar && look.avatar.filename}
            <div class={cn('w-8 h-8', 'rounded-full shrink-0')}>
              <CmsImage
                image={look.avatar}
                class={cn('w-8 h-8', 'rounded-full object-cover')}
                options={{ size: [32, 0] }}
              />
            </div>
          {/if}
          <div class="self-end">
            <p class="text-highlight">
              {#if look.username}
                <span class="font-semibold">{look.username}</span>
              {/if}

              {#if look.username && look.date}
                <span>•</span>
              {/if}

              {#if look.date}
                {@const date = new Date(look.date || '')}
                <span>
                  {date.toLocaleDateString($lang, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              {/if}
            </p>

            {#if look.caption}
              <div
                class={cn('transition-all ease-in-out', !isExpanded && 'line-clamp-1')}
                bind:this={content}
                style:height={height + 'px'}
              >
                <p class={cn('text-highlight')}>
                  {look.caption}
                </p>
              </div>
            {/if}
          </div>
        </div>
      {/if}

      {#if variant == 'drawer' && productHandles && productHandles.length > 1}
        <div class="hidden lg:flex shrink-0 h-fit gap-2 self-end">
          <SliderButton
            variant="ghost"
            icon="chevron-left-bold"
            ariaLabel="Slider Button Left"
            disabled={carouselIdx === 0}
            on:click={() => handleCarousel('left')}
          />

          <SliderButton
            variant="ghost"
            icon="chevron-right-bold"
            ariaLabel="Slider Button Right"
            disabled={carouselIdx === productHandles.length - 1}
            on:click={() => handleCarousel('right')}
          />
        </div>
      {/if}
    </div>

    <!-- Product Cards -->
    <div
      class={cn(
        'flex w-full snap-x snap-mandatory gap-2 overflow-x-auto pr-2 hide-scrollbar motion-reduce:transition-none'
      )}
      bind:this={carousel}
      use:snapCenterScrollPosition={(i) => {
        carouselIdx = i;
      }}
      on:scrollend={() => {
        analyticsEvent({
          event: AnalyticsEvents.LOOK,
          id: look.event_id
        });
        analyticsEvent({
          event: AnalyticsEvents.LOOK_SCROLL_PRODUCTS,
          id: look.event_id
        });
      }}
      style:--card-min-w="{CARD_WIDTH}px"
      role="region"
    >
      {#each productHandles || [] as handle, i}
        {#if handle}
          <QuickAddCardLoader {handle} enabled={false} let:loading let:product let:load>
            <QuickAddCard
              class={cn(
                'min-w-[var(--card-min-w)] flex-1 snap-center snap-always',
                i === 0 && 'ml-2'
              )}
              on:intersect={load}
              on:productClick={(event) => {
                const product = event.detail;
                analyticsEvent({
                  event: AnalyticsEvents.LOOK,
                  id: look.event_id
                });
                analyticsEvent({
                  event: AnalyticsEvents.LOOK_CLICK_PRODUCT,
                  id: look.event_id,
                  product: product
                });
              }}
              on:addedToCart={(event) => {
                const product = event.detail;
                analyticsEvent({
                  event: AnalyticsEvents.LOOK,
                  id: look.event_id
                });
                analyticsEvent({
                  event: AnalyticsEvents.LOOK_ADD_TO_CART,
                  id: look.event_id,
                  product: product
                });
              }}
              {loading}
              {product}
              productHandle={handle}
              addDrawerTargetId={targetId}
              location={AddToCartLocations.SHOP_THE_LOOK}
              emptyStateImage={markersSrc ? markersImageAsset : sbImageAsset}
              size="sm"
            />
          </QuickAddCardLoader>
        {/if}
      {/each}
    </div>
  </div>
{:else}
  <div class="absolute bottom-0 w-full z-10 p-2">
    <Button
      name={'Shop the Look'}
      variant="secondary"
      class="backdrop-blur"
      on:click={(e) => {
        e.stopPropagation();
        dispatch('click');
      }}
    >
      {'Shop the Look'}
    </Button>
  </div>
{/if}
